import { Alert, Box, Button, Link, TextField, Typography } from '@mui/material';
import { JSX, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../../../contexts/AuthContext';
import { forgotPasswordLink } from '../../../../library/constants';
import { fieldProps, useValidatedForm } from '../../../../library/formValidation';
import ResourcesIds from '../../../../resources/ResourcesIds';
import { createLoginSchema } from '../../../auth/loginSchema';
import { actionsStyles, fieldStyles, formAlertStyles, hintStyles } from './existingAccountForm.styles';

interface LoginValues {
    emailAddress: string;
    password: string;
}

interface ExistingAccountFormProps {
    clinicName: string;
    onSignedIn: () => void;
}

/**
 * A patient who already has an account signs in here, in place, and the booking they have
 * just put together goes off at once.
 *
 * In place rather than through `/login`, so the page they chose on never unmounts -- the
 * choice is in the wizard's state, and the draft is only the fallback. `LoginForm` is left
 * untouched by this feature for the same reason.
 *
 * A failed sign-in says only that it failed, the way the login page does: on a clinic's own
 * domain an account with no membership there is refused exactly like a wrong password, so this
 * form cannot be used to learn who is a patient where. The hint beneath it points such a person
 * to the other door, which handles an existing address by email.
 */
export const ExistingAccountForm = ({ clinicName, onSignedIn }: ExistingAccountFormProps): JSX.Element => {
    const { t: translate } = useTranslation();
    const { login } = useAuth();
    const [failed, setFailed] = useState<boolean>(false);
    const [isSigningIn, setIsSigningIn] = useState<boolean>(false);

    const schema = useMemo(() => createLoginSchema(translate), [translate]);

    const { register, handleSubmit, formState: { errors, isValid } } = useValidatedForm<LoginValues>(schema, {
        defaultValues: { emailAddress: '', password: '' },
    });

    const onValid = async (values: LoginValues): Promise<void> => {
        setFailed(false);
        setIsSigningIn(true);

        try {
            const response = await login({ emailAddress: values.emailAddress.trim(), password: values.password });

            // Staff are sent to their own landing page by the gate above; only a patient books.
            if (response.role === 'CUSTOMER') {
                onSignedIn();
            }
        } catch {
            setFailed(true);
        } finally {
            setIsSigningIn(false);
        }
    };

    return (
        <Box component="form" noValidate onSubmit={handleSubmit(onValid)}>
            <Typography sx={hintStyles}>
                {translate(ResourcesIds.PUBLIC_BOOKING_LOGIN_HINT)}
            </Typography>

            {failed && (
                <Alert severity="warning" sx={formAlertStyles}>
                    {translate(ResourcesIds.LOGIN_FAILED_DEFAULT)}{' '}
                    {translate(ResourcesIds.PUBLIC_BOOKING_LOGIN_FAILED_HINT, { clinic: clinicName })}
                </Alert>
            )}

            <TextField
                fullWidth
                type="email"
                autoComplete="email"
                sx={fieldStyles}
                label={translate(ResourcesIds.EMAIL_LABEL)}
                {...register('emailAddress')}
                {...fieldProps(errors, 'emailAddress')}
            />
            <TextField
                fullWidth
                type="password"
                autoComplete="current-password"
                sx={fieldStyles}
                label={translate(ResourcesIds.PASSWORD_LABEL)}
                {...register('password')}
                {...fieldProps(errors, 'password')}
            />

            <Box sx={actionsStyles}>
                <Button type="submit" variant="contained" disabled={!isValid || isSigningIn}>
                    {translate(ResourcesIds.SIGN_IN_BUTTON)}
                </Button>
                <Link component={RouterLink} to={forgotPasswordLink} target="_blank" rel="noopener">
                    {translate(ResourcesIds.FORGOT_PASSWORD_TEXT)}
                </Link>
            </Box>
        </Box>
    );
};
